// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useMemo, useState } from 'react';
import "./ContentTypeAnalytics.scss";
import { useTranslation } from "react-i18next";
import { useTable, useSortBy, useExpanded } from 'react-table';
import { format } from "date-fns";
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';

// Register ChartJS components
ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

export default function ContentTypeAnalytics({ logs, users, getUserDisplayName }) {
  const { t } = useTranslation();
  const [displayLimit, setDisplayLimit] = useState(20);
  const [isLoading, setIsLoading] = useState(false);

  // Process logs to extract select_content events and their content_types
  const processedData = useMemo(() => {
    // Filter only select_content events that have content_type parameter
    const selectContentLogs = logs.filter(log => 
      log.event_name === "select_content" && 
      log.event_params && 
      log.event_params.content_type
    );

    if (selectContentLogs.length === 0) {
      return {
        contentTypeGroups: [],
        contentTypeCount: {},
        totalEvents: 0
      };
    }

    // Group by content_type
    const groupedByContentType = {};
    const contentTypeCount = {};

    selectContentLogs.forEach(log => {
      const contentType = log.event_params.content_type;
      
      // Initialize groups if needed
      if (!groupedByContentType[contentType]) {
        groupedByContentType[contentType] = {
          contentType,
          events: [],
          users: new Set(),
          sources: new Set()
        };
      }
      
      // Add log to group
      groupedByContentType[contentType].events.push(log);
      groupedByContentType[contentType].users.add(log.user);
      groupedByContentType[contentType].sources.add(log.source);
      
      // Count content types
      contentTypeCount[contentType] = (contentTypeCount[contentType] || 0) + 1;
    });

    // Convert to array and finalize data
    const contentTypeGroups = Object.values(groupedByContentType).map(group => ({
      ...group,
      userCount: group.users.size,
      users: Array.from(group.users),
      sourceCount: group.sources.size,
      sources: Array.from(group.sources),
      eventCount: group.events.length
    })).sort((a, b) => b.eventCount - a.eventCount);

    return {
      contentTypeGroups,
      contentTypeCount,
      totalEvents: selectContentLogs.length
    };
  }, [logs]);

  // Chart data for content type distribution
  const chartData = useMemo(() => {
    if (processedData.contentTypeGroups.length === 0) {
      return {
        labels: [],
        datasets: [{ data: [] }]
      };
    }

    // Color palette
    const colorPalette = [
      'rgba(66, 144, 245, 0.8)',
      'rgba(63, 185, 80, 0.8)',
      'rgba(219, 97, 162, 0.8)',
      'rgba(187, 128, 9, 0.8)',
      'rgba(97, 113, 216, 0.8)',
      'rgba(71, 193, 191, 0.8)',
      'rgba(250, 130, 49, 0.8)',
      'rgba(180, 90, 212, 0.8)'
    ];

    // Generate chart data
    const labels = Object.keys(processedData.contentTypeCount);
    const data = Object.values(processedData.contentTypeCount);
    
    // Limit to top 8 for chart visibility, combine others
    let chartLabels = labels;
    let chartData = data;
    
    if (labels.length > 8) {
      chartLabels = [...labels.slice(0, 7), 'Others'];
      chartData = [...data.slice(0, 7), data.slice(7).reduce((sum, val) => sum + val, 0)];
    }
    
    return {
      labels: chartLabels,
      datasets: [
        {
          data: chartData,
          backgroundColor: colorPalette.slice(0, chartLabels.length),
          borderColor: 'white',
          borderWidth: 1
        }
      ]
    };
  }, [processedData]);

  // Limited data to display
  const limitedData = useMemo(() => {
    return processedData.contentTypeGroups.slice(0, displayLimit);
  }, [processedData.contentTypeGroups, displayLimit]);

  const handleLoadMore = () => {
    setIsLoading(true);
    // Simulate loading delay
    setTimeout(() => {
      setDisplayLimit(prev => prev + 20);
      setIsLoading(false);
    }, 300);
  };

  // Table columns definition
  const columns = useMemo(() => [
    {
      Header: t('content_type') || 'Content Type',
      accessor: 'contentType',
      width: 200,
    },
    {
      Header: t('events') || 'Events',
      accessor: 'eventCount',
      width: 100,
      Cell: ({ value }) => (
        <div className="event-count">
          <span className="count-badge">{value}</span>
        </div>
      )
    },
    {
      Header: t('unique_users') || 'Unique Users',
      accessor: 'userCount',
      width: 150,
      Cell: ({ value }) => (
        <div className="user-count">
          <span className="count-badge users">{value}</span>
        </div>
      )
    },
    {
      Header: t('sources') || 'Sources',
      accessor: 'sources',
      Cell: ({ value }) => (
        <div className="sources-container">
          {value.map(source => (
            <span 
              key={source} 
              className={`source-badge ${source}`}
            >
              {source}
            </span>
          ))}
        </div>
      )
    }
  ], [t]);

  // Row expansion details
  const renderRowSubComponent = React.useCallback(({ row }) => {
    return (
      <div className="content-type-detail">
        <h4>{t('users_of_content_type') || `Users of content type: ${row.original.contentType}`}</h4>
        <div className="user-list">
          {row.original.users.map(userId => (
            <div key={userId} className="user-item">
              <div className="user-name">{getUserDisplayName(userId)}</div>
              <div className="user-events-count">
                {row.original.events.filter(event => event.user === userId).length} events
              </div>
            </div>
          ))}
        </div>
        
        <h4>{t('recent_events') || 'Recent Events'}</h4>
        <table className="events-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>User</th>
              <th>Source</th>
              <th>Parameters</th>
            </tr>
          </thead>
          <tbody>
            {[...row.original.events]
              .sort((a, b) => b.timestamp.toDate() - a.timestamp.toDate())
              .slice(0, 10)
              .map((event, idx) => (
                <tr key={idx}>
                  <td className="time-cell">{format(event.timestamp.toDate(), "MMM dd, yyyy HH:mm:ss")}</td>
                  <td className="user-cell">{getUserDisplayName(event.user)}</td>
                  <td className="source-cell">{event.source}</td>
                  <td className="params-cell">
                    {event.event_params && Object.keys(event.event_params).length > 0 ? (
                      <div className="params-grid">
                        {Object.entries(event.event_params).map(([key, value]) => (
                          <div key={key} className="param-item">
                            <span className="param-key">{key}:</span>
                            <span className="param-value">
                              {typeof value === 'object' 
                                ? JSON.stringify(value) 
                                : String(value)
                              }
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="no-params">No parameters</span>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    );
  }, [getUserDisplayName, t]);

  // Initialize table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
    state: { expanded },
  } = useTable(
    { 
      columns, 
      data: limitedData,
      initialState: {
        sortBy: [{ id: 'eventCount', desc: true }],
      }
    },
    useSortBy,
    useExpanded
  );

  // Empty state handling
  if (processedData.contentTypeGroups.length === 0) {
    return (
      <div className="content-type-analytics empty-state">
        <h3>{t('content_type_analytics') || 'Content Type Analytics'}</h3>
        <p>{t('no_select_content_events') || 'No select_content events with content_type parameter found in the selected logs.'}</p>
      </div>
    );
  }

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        titleColor: '#24292e',
        bodyColor: '#24292e',
        borderColor: '#eaecef',
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        boxPadding: 6,
        usePointStyle: true,
        callbacks: {
          label: function(context) {
            const value = context.raw;
            const total = context.dataset.data.reduce((acc, curr) => acc + curr, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  return (
    <div className="content-type-analytics">
      <h3>{t('content_type_analytics') || 'Content Type Analytics'}</h3>
      
      <div className="content-analytics-header">
        <div className="content-summary">
          <div className="summary-item">
            <div className="summary-label">{t('total_select_content_events') || 'Total select_content Events'}</div>
            <div className="summary-value">{processedData.totalEvents}</div>
          </div>
          <div className="summary-item">
            <div className="summary-label">{t('unique_content_types') || 'Unique Content Types'}</div>
            <div className="summary-value">{processedData.contentTypeGroups.length}</div>
          </div>
        </div>
        
        <div className="chart-container">
          <Pie data={chartData} options={chartOptions} />
        </div>
      </div>
      
      <div className="table-wrapper">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                <th className="expander-column"></th>
                {headerGroup.headers.map(column => (
                  <th 
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className={column.isSorted ? (column.isSortedDesc ? 'sorted-desc' : 'sorted-asc') : ''}
                  >
                    {column.render('Header')}
                    <span className="sort-indicator">
                      {column.isSorted ? (column.isSortedDesc ? ' ▼' : ' ▲') : ''}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);
              const isExpanded = expanded[row.id];
              return (
                <React.Fragment key={row.getRowProps().key}>
                  <tr {...row.getRowProps()} className={isExpanded ? 'row-expanded' : ''}>
                    <td className="expander-column">
                      <button 
                        {...row.getToggleRowExpandedProps()}
                        className={`expander-button ${isExpanded ? 'expanded' : 'collapsed'}`}
                      >
                        {isExpanded ? '▼' : '▶'}
                      </button>
                    </td>
                    {row.cells.map(cell => (
                      <td {...cell.getCellProps()}>
                        {cell.render('Cell')}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && (
                    <tr className="expanded-row">
                      <td colSpan={visibleColumns.length + 1}>
                        {renderRowSubComponent({ row })}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {displayLimit < processedData.contentTypeGroups.length && (
        <div className="load-more-container">
          <button 
            className="load-more-button" 
            onClick={handleLoadMore}
            disabled={isLoading}
          >
            {isLoading ? 
              (t('loading') || 'Loading...') : 
              (t('load_more') || `Load More (${displayLimit}/${processedData.contentTypeGroups.length})`)}
          </button>
        </div>
      )}
    </div>
  );
} 